import {toast} from 'react-toastify';
import {ScreenTracker} from '../../../utils/ScreenTracker';
import {
    DV360_CHANNEL, FACEBOOK_CHANNEL, GOOGLE_CHANNEL, LINKEDIN_CHANNEL, TIKTOK_CHANNEL,
} from '../../channels/channels';
import {platformName} from '../connections_v2/platformsText';
import {connectedDestinationFor} from '../link_acocunts/connectedDestination';

/**
 * Los textos y las reglas de «Reconectar una cuenta» que comparten el modal, Conexiones › Cuentas, la
 * fila del Tablero, Vincular cuenta y los callbacks de cada canal (tanda «Reconexión de pauta»,
 * UD-20 y UD-21; `copy.md` de la tanda). Reciben el `t` de quien dibuja: se testean sin React.
 *
 * Todo lo que se interpola es dato (el nombre de una cuenta, de una persona, de una conexión), y
 * i18next escapa `/ & < > " '` a entidades si no se le pide lo contrario: por eso `RAW` va en cada uno.
 */

const NS = 'reconnect_account';
const RAW = {escapeValue: false};

/** Los canales de pauta con conexión por usuario: los que reconectan con el flujo nuevo (contrato, canales). */
export const PAID_RECONNECT_CHANNELS = [FACEBOOK_CHANNEL, GOOGLE_CHANNEL, TIKTOK_CHANNEL, LINKEDIN_CHANNEL, DV360_CHANNEL];

export function isPaidReconnectChannel(channel) {
    return PAID_RECONNECT_CHANNELS.includes(channel === 'META' ? FACEBOOK_CHANNEL : channel);
}

// Como lo dice la marca, corto (P-4): «Reconectar con Meta», «el usuario de Meta». `platformName`
// dice «Meta Ads» y «TikTok Ads», que es el nombre del canal en las listas.
const SHORT_NAMES = {
    [FACEBOOK_CHANNEL]: 'Meta',
    META: 'Meta',
    [GOOGLE_CHANNEL]: 'Google Ads',
    [TIKTOK_CHANNEL]: 'TikTok',
    [LINKEDIN_CHANNEL]: 'LinkedIn',
    [DV360_CHANNEL]: 'DV360',
};

export function reconnectPlatformName(channel) {
    return SHORT_NAMES[channel] || platformName(channel);
}

// ------------------------------------------------------------------ quién ve la cuenta (copy §1)

/**
 * Lo que se sabe de quién ve la cuenta, del escalón más útil al menos: el usuario de la plataforma
 * (Meta y TikTok lo dan), quién de la agencia la conectó (conexiones desde oct-2026) o el nombre de la
 * conexión. `source` trae los campos como los manda el backend en Cuentas y en el Tablero. `null` si no
 * se sabe nada: entonces no se dice nada.
 */
export function ownerOf(source) {
    if (!source) return null;
    if (source.platform_user_name) return {kind: 'platform_user', name: source.platform_user_name};
    if (source.connected_by_user_name) return {kind: 'connected_by', name: source.connected_by_user_name};
    if (source.agency_channel_credential_label) return {kind: 'label', name: source.agency_channel_credential_label};
    return null;
}

/** ¿La frase de §1 nombra a una PERSONA? Sólo entonces el modal dice «Si esa persona ya no está…». */
export function ownerNamesPerson(source) {
    const owner = ownerOf(source);
    return !!owner && owner.kind !== 'label';
}

/** La frase entera («La ve el usuario de Meta Cami.»), o `null`. */
export function ownerSentence(t, channel, source) {
    const owner = ownerOf(source);
    if (!owner) return null;
    const platform = reconnectPlatformName(channel);
    if (owner.kind === 'platform_user') return t(`${NS}.owner.platform_user`, {user: owner.name, platform, interpolation: RAW});
    if (owner.kind === 'connected_by') return t(`${NS}.owner.connected_by`, {person: owner.name, interpolation: RAW});
    return t(`${NS}.owner.label`, {label: owner.name, interpolation: RAW});
}

/** La versión corta, para filas y para la línea del Tablero («La ve Cami»), o `null`. */
export function ownerShort(t, source) {
    const owner = ownerOf(source);
    if (!owner) return null;
    if (owner.kind === 'platform_user') return t(`${NS}.owner_short.platform_user`, {user: owner.name, interpolation: RAW});
    if (owner.kind === 'connected_by') return t(`${NS}.owner_short.connected_by`, {person: owner.name, interpolation: RAW});
    return t(`${NS}.owner_short.label`, {label: owner.name, interpolation: RAW});
}

// ------------------------------------------------------------------ la fecha

/**
 * La fecha corta del idioma, desde un ISO: «23/10, 14:05» en español, «Oct 23, 14:05» en inglés, en la
 * hora del navegador. `null` si no hay fecha o no se entiende (un backend viejo manda sólo el texto
 * de `TimeFormatter`, «Hoy, 16:04»): entonces la pantalla usa su variante sin fecha (copy §7).
 */
export function formatShortDate(iso, language) {
    // Sólo un ISO: `new Date()` acepta «Oct 23, 2026, 16:04» en V8 y otros textos en otros navegadores.
    if (typeof iso !== 'string' || !/^\d{4}-\d{2}-\d{2}T/.test(iso)) return null;
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return null;
    if (String(language || 'es').toLowerCase().startsWith('en')) {
        return date.toLocaleString('en-US', {month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hourCycle: 'h23'});
    }
    return date.toLocaleString('es-AR', {day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23'});
}

// ------------------------------------------------------------------ medición (contrato §6)

/** `reconnect_<acción>_<canal>`, con el canal en minúscula como los eventos de UD-9. */
export function reconnectEventName(action, channel) {
    return `reconnect_${action}_${String(channel).toLowerCase()}`;
}

export function recordReconnectEvent(action, channel) {
    ScreenTracker.recordEvent(reconnectEventName(action, channel));
}

// ------------------------------------------------------------------ al volver de la plataforma (copy §3)

export const ORIGIN_ACCOUNT = 'account';
export const ORIGIN_CREDENTIAL = 'credential';
export const ORIGIN_NEW = 'new';

/**
 * Lo que el pedido de conexión devolvió sobre la reconexión (contrato §2.2), o `null` si el backend no
 * lo dice (uno viejo): entonces cada canal muestra su aviso de siempre.
 */
export function reconnectResultOf(response) {
    const data = response && response.data;
    if (!data || (data.outcome !== 'renewed' && data.outcome !== 'created')) return null;
    const account = data.reconnected_account || null;
    return {
        outcome: data.outcome,
        connectionId: data.connection_id === undefined ? null : data.connection_id,
        platformUserName: data.platform_user_name || null,
        label: data.connection_label || null,
        account: account ? {
            id: account.ad_account_id,
            name: account.name || '',
            hasAccess: account.has_access,
            moved: account.moved,
        } : null,
    };
}

/** De dónde arrancó la conexión, según la nota que dejó quien la empezó. */
export function originOf(note) {
    if (note && note.adAccountId) return ORIGIN_ACCOUNT;
    if (note && note.credentialId) return ORIGIN_CREDENTIAL;
    return ORIGIN_NEW;
}

/**
 * El aviso al volver (M3), o `null` para que el canal diga el de siempre (una conexión nueva, o un
 * backend viejo). `{kind: 'success' | 'info' | 'warning', text}`.
 *
 * - Desde el «Reconectar» de una cuenta: si el usuario que entró no la ve, se dice; si la cuenta quedó
 *   en otra conexión (`moved`; sin el campo, `outcome: created`), «pasa a la conexión de…»; si no,
 *   «renovamos la conexión de…».
 * - Renovar una conexión desde Plataformas: «renovamos la conexión de…».
 * - «Nueva conexión» con un usuario que ya estaba: el rechazo de antes, ahora como aviso.
 */
export function reconnectNotice(t, channel, result, origin) {
    if (!result) return null;
    const platform = reconnectPlatformName(channel);
    const user = result.platformUserName;
    const account = result.account;

    if (origin === ORIGIN_ACCOUNT && account) {
        const opts = {user, account: account.name, platform, label: result.label, interpolation: RAW};
        if (account.hasAccess === false) {
            return {kind: 'warning', text: t(`${NS}.result.${user ? 'no_access' : 'no_access_unnamed'}`, opts)};
        }
        const moved = typeof account.moved === 'boolean' ? account.moved : result.outcome === 'created';
        if (moved) {
            let key = 'moved_unnamed';
            if (user) key = 'moved';
            else if (result.label) key = 'moved_label';
            return {kind: 'success', text: t(`${NS}.result.${key}`, opts)};
        }
        return {kind: 'success', text: t(`${NS}.result.${user ? 'renewed' : 'renewed_unnamed'}`, opts)};
    }

    if (result.outcome !== 'renewed') return null;
    if (origin === ORIGIN_NEW) {
        const key = result.label ? 'already_connected' : 'already_connected_unnamed';
        return {kind: 'info', text: t(`${NS}.result.${key}`, {platform, label: result.label, interpolation: RAW})};
    }
    return {kind: 'success', text: t(`${NS}.result.${user ? 'renewed' : 'renewed_unnamed'}`, {user, interpolation: RAW})};
}

/** El evento de la vuelta: sólo cuando arrancó en el modal de una cuenta (contrato §9, rp-fe). */
export function recordReconnectResult(channel, result, origin) {
    if (origin !== ORIGIN_ACCOUNT || !result) return;
    recordReconnectEvent(`result_${result.outcome}`, channel);
}

/** Un aviso sin cierre automático si hay algo que hacer; con cierre si sólo confirma. */
export function announceReconnectNotice(notice) {
    if (!notice) return;
    const show = toast[notice.kind] || toast.success;
    show(notice.text, notice.kind === 'warning' ? {autoClose: false} : {autoClose: 8000});
}

// Meta vuelve con una recarga de página (`window.location`), que se lleva los toasts: el aviso espera
// en `sessionStorage` a que Conexiones lo muestre al montar.
const DEFERRED_NOTICE_KEY = 'pending_reconnect_notice';

export function deferReconnectNotice(notice) {
    if (!notice) return;
    try {
        window.sessionStorage.setItem(DEFERRED_NOTICE_KEY, JSON.stringify({kind: notice.kind, text: notice.text}));
    } catch (error) {
        // Sin sessionStorage la conexión quedó igual; sólo no se dice cómo.
    }
}

export function takeDeferredReconnectNotice() {
    let notice = null;
    try {
        notice = JSON.parse(window.sessionStorage.getItem(DEFERRED_NOTICE_KEY));
        window.sessionStorage.removeItem(DEFERRED_NOTICE_KEY);
    } catch (error) {
        notice = null;
    }
    return notice && typeof notice.text === 'string' ? notice : null;
}

/**
 * A dónde volver: Conexiones con el acceso que quedó (`credential`). Si la conexión arrancó en una
 * cuenta y el backend no dice que la movió (uno viejo), Conexiones la reasigna con
 * `reconnect_ad_account_id`, como antes. Con el backend nuevo, la reasignación ya la hizo él.
 */
export function destinationAfterConnect(channel, response, note) {
    const base = connectedDestinationFor(channel, response);
    const result = reconnectResultOf(response);
    if (!note || !note.adAccountId || (result && result.account)) return base;
    return `${base}&reconnect_ad_account_id=${encodeURIComponent(note.adAccountId)}`;
}
